import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Loader2, Building2, PackageOpen, Info } from "lucide-react";
import { toast } from "sonner";

const SURPLUS_CATEGORIES = ["Acabados", "Acabados acústicos", "Pintura", "Loseta", "Plafón", "Madera", "Cable", "Otros materiales"];
const EQUIP_STATUS = [
  { value: "available", label: "Disponible" },
  { value: "in_use", label: "En uso" },
  { value: "soon", label: "Próximo a desocuparse" },
];
const MATERIAL_CONDITIONS = ["Nuevo", "Buen estado", "Usado", "Requiere revisión"];

export default function UploadAsset() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [assetType, setAssetType] = useState(""); // "equipment" | "surplus"
  const [projects, setProjects] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "", quantity: 1, unit: "unit", category: "Acabados",
    equip_status: "available", available_date: today,
    location: "", condition: "Buen estado", comments: "",
  });

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data));
  }, []);

  const assignedProject = projects.find((p) => p.id === user?.current_project_id) || null;

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onEquipStatus = (v) => {
    // Sprint 9: cuando el estatus es "disponible" la fecha se autocompleta con hoy y se deshabilita.
    setForm((f) => ({ ...f, equip_status: v, available_date: v === "available" ? today : "" }));
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: data.path, name: f.name }]);
      }
      toast.success("Fotos subidas");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falló la subida");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!assetType) { toast.error("Selecciona el tipo de activo"); return; }
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!user?.current_project_id) {
      toast.error("Asígnate un proyecto en tu perfil antes de cargar activos.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      const isSurplus = assetType === "surplus";
      fd.append("name", form.name);
      fd.append("quantity", String(form.quantity));
      // El backend fuerza project_id al proyecto asignado; se incluye por compatibilidad.
      fd.append("project_id", user.current_project_id);
      fd.append("is_surplus", String(isSurplus));
      fd.set("photos", photos.map((p) => p.path).join(","));

      if (isSurplus) {
        fd.append("category", form.category);
        fd.append("unit", form.unit);
        fd.append("status", "available");
        fd.append("condition", form.condition);
        fd.append("description", form.comments);
      } else {
        fd.append("category", "Equipamiento provisional");
        fd.append("unit", "unit");
        fd.append("location", form.location);
        // "Próximo a desocuparse" => available with a future date
        const status = form.equip_status === "soon" ? "available" : form.equip_status;
        fd.append("status", status);
        fd.append("available_date", form.available_date || "");
      }
      await api.post("/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isSurplus ? "Material sobrante publicado" : "Equipamiento registrado");
      nav(isSurplus ? "/surplus" : "/search");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir el activo");
    } finally {
      setSubmitting(false);
    }
  };

  const TYPE_OPTIONS = [
    { key: "equipment", title: "Equipamiento provisional", desc: "Activos operativos reutilizables que se mueven entre obras.", icon: Building2, accent: "#2563EB" },
    { key: "surplus", title: "Material sobrante", desc: "Materiales de construcción sobrantes con precio interno de reúso.", icon: PackageOpen, accent: "#16A34A" },
  ];

  return (
    <div className="space-y-8 max-w-3xl" data-testid="upload-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Función 04</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Subir activo.</h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Tipo de activo (required, first) */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="asset-type-section">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D] mb-3">Tipo de activo *</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {TYPE_OPTIONS.map((t) => {
              const active = assetType === t.key;
              return (
                <button
                  type="button" key={t.key} onClick={() => setAssetType(t.key)}
                  className={`text-left rounded-2xl border-2 p-5 transition-all duration-200 ${active ? "shadow-[0_12px_30px_rgba(22,163,74,0.12)]" : "border-[#E2E8F0] hover:border-[#CBD5E1]"}`}
                  style={active ? { borderColor: t.accent, background: `${t.accent}08` } : {}}
                  data-testid={`type-${t.key}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0`} style={{ borderColor: active ? t.accent : "#CBD5E1" }}>
                      {active && <span className="w-2 h-2 rounded-full" style={{ background: t.accent }} />}
                    </span>
                    <div>
                      <div className="font-display font-bold text-[#0A2540] flex items-center gap-2"><t.icon size={16} style={{ color: t.accent }} /> {t.title}</div>
                      <p className="text-xs text-[#5C6A7D] mt-1">{t.desc}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {assetType && (
          <>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="asset-fields">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">
                {assetType === "surplus" ? "Detalles del material sobrante" : "Detalles del equipamiento"}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nombre *</Label>
                  <Input required value={form.name} onChange={(e) => upd({ name: e.target.value })} className="rounded-sm h-11" data-testid="asset-name-input" />
                </div>

                {/* Surplus category */}
                {assetType === "surplus" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Categoría</Label>
                    <Select value={form.category} onValueChange={(v) => upd({ category: v })}>
                      <SelectTrigger className="rounded-sm h-11" data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{SURPLUS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Cantidad *</Label>
                  <Input type="number" min={1} required value={form.quantity} onChange={(e) => upd({ quantity: parseInt(e.target.value) || 1 })} className="rounded-sm h-11" data-testid="asset-quantity-input" />
                </div>

                {/* Surplus unit */}
                {assetType === "surplus" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Unidad</Label>
                    <Input value={form.unit} onChange={(e) => upd({ unit: e.target.value })} placeholder="p. ej. m², cajas, kg" className="rounded-sm h-11" data-testid="asset-unit-input" />
                  </div>
                )}

                {/* Project (Sprint 9: derivado del perfil, ya no editable en carga) */}
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">{assetType === "surplus" ? "Proyecto de origen" : "Proyecto"}</Label>
                  {assignedProject ? (
                    <div className="rounded-sm h-11 flex items-center justify-between px-3 bg-[#F0FAF4] border border-[#16A34A]/30" data-testid="asset-assigned-project">
                      <span className="text-sm font-semibold text-[#0A2540]">{assignedProject.name}</span>
                      <Link to={`/employees/${user?.id}`} className="text-[10px] uppercase tracking-[0.15em] text-[#16A34A] hover:underline">Cambiar en perfil</Link>
                    </div>
                  ) : (
                    <div className="rounded-sm h-auto p-3 bg-[#FEF3C7] border border-[#F59E0B]/40 text-xs text-[#92400E] flex items-start gap-2" data-testid="asset-no-project-warning">
                      <Info size={14} className="mt-0.5 shrink-0" />
                      <span>Aún no tienes un proyecto asignado. <Link to={`/employees/${user?.id}`} className="font-semibold underline hover:text-[#78350F]">Asígnate uno en tu perfil</Link> antes de cargar activos.</span>
                    </div>
                  )}
                </div>

                {/* Equipment: estado */}
                {assetType === "equipment" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Estado</Label>
                    <Select value={form.equip_status} onValueChange={onEquipStatus}>
                      <SelectTrigger className="rounded-sm h-11" data-testid="asset-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{EQUIP_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                {/* Equipment: fecha estimada de disponibilidad */}
                {assetType === "equipment" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Fecha estimada de disponibilidad</Label>
                    <Input
                      type="date"
                      value={form.available_date}
                      onChange={(e) => upd({ available_date: e.target.value })}
                      disabled={form.equip_status === "available"}
                      className="rounded-sm h-11 disabled:opacity-60 disabled:bg-[#FAFAFA] disabled:cursor-not-allowed"
                      data-testid="asset-date-input"
                    />
                    {form.equip_status === "available" && (
                      <p className="text-[10px] text-[#5C6A7D]">Al estar "disponible", se registra automáticamente con la fecha de hoy.</p>
                    )}
                  </div>
                )}

                {/* Equipment: ubicación */}
                {assetType === "equipment" && (
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Ubicación</Label>
                    <Input value={form.location} onChange={(e) => upd({ location: e.target.value })} className="rounded-sm h-11" data-testid="asset-location-input" />
                  </div>
                )}

                {/* Surplus: estado del material */}
                {assetType === "surplus" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Estado del material</Label>
                    <Select value={form.condition} onValueChange={(v) => upd({ condition: v })}>
                      <SelectTrigger className="rounded-sm h-11" data-testid="asset-condition-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{MATERIAL_CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                {/* Surplus: comentarios */}
                {assetType === "surplus" && (
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Comentarios</Label>
                    <Textarea value={form.comments} onChange={(e) => upd({ comments: e.target.value })} rows={3} className="rounded-sm" data-testid="asset-comments-input" />
                  </div>
                )}
              </div>

              {assetType === "surplus" && (
                <p className="text-xs text-[#5C6A7D] bg-[#F0FAF4] border border-[#16A34A]/20 rounded-lg px-3 py-2">
                  El precio interno de reúso será asignado posteriormente por el Departamento de Costos.
                </p>
              )}
            </div>

            {/* Photos */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D] mb-3">Fotos</div>
              <label className="block border border-dashed border-[#0A2540]/30 rounded-sm p-6 text-center cursor-pointer hover:bg-[#FAFAFA] transition-colors" data-testid="photo-upload-area">
                <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="photo-input" />
                {uploading ? <Loader2 className="mx-auto animate-spin text-[#0A2540]" /> : <Upload className="mx-auto text-[#0A2540]" size={20} strokeWidth={1.5} />}
                <div className="mt-2 text-sm font-semibold text-[#0A2540]">Haz clic para subir imágenes</div>
                <div className="text-xs text-[#5C6A7D] mt-1">PNG, JPG, WEBP</div>
              </label>
              {photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-4" data-testid="photo-previews">
                  {photos.map((p, i) => (
                    <div key={i} className="relative aspect-square">
                      <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover rounded-sm border border-[#E2E8F0]" />
                      <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 w-6 h-6 bg-[#0A2540] text-white rounded-full flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-12 font-semibold tracking-wide" data-testid="submit-asset-button">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : "Subir activo"}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
